import { config, url } from './config';
import { obterGeo } from './usuario';

// Buscas recentes: a v2 guarda o par cargo + local; as chaves do v1 (eventoBuscarVagaRedirect.js) continuam
// sendo gravadas, para v1 e v2 mostrarem as mesmas buscas.
const _chaveCargos = 'vagasBuscasRecentesCargo';
const _chaveLocais = 'vagasBuscasRecentesLocalizacao';
const _chavePares = 'v2BuscasRecentes';
const _chaveEscolhido = 'v2LocalEscolhido';

function _lerLista(chave) {
    try {
        const lista = JSON.parse(localStorage.getItem(chave) || '[]');
        return Array.isArray(lista) ? lista : [];
    } catch {
        return [];
    }
}

function _gravar(chave, valor) {
    try {
        localStorage.setItem(chave, JSON.stringify(valor));
    } catch {
        // Armazenamento bloqueado: segue sem guardar.
    }
}

function _uid() {
    return (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2);
}

// Texto salvo cru pelo v1 pode ser "Carregando sugestões..."; isso não é busca.
const _valido = texto => typeof texto === 'string' && texto.trim() && !/(\.\.\.|…)$/.test(texto.trim());

// [[cargo, local], ...], a mais recente primeiro. Sem pares da v2, usa os cargos do v1 (sem local).
export function buscasRecentes() {
    const pares = _lerLista(_chavePares).filter(p => Array.isArray(p) && _valido(p[0]));
    if (pares.length) {
        return pares;
    }
    return _lerLista(_chaveCargos).map(x => x?.cargo).filter(_valido).reverse().map(c => [c, '']);
}

export function locaisRecentes() {
    const daV2 = _lerLista(_chavePares).map(p => p && p[1]).filter(_valido);
    const doV1 = _lerLista(_chaveLocais).map(x => x?.nome).filter(_valido).reverse();
    return [...new Set([...daV2, ...doV1])];
}

export function guardarBuscaRecente(cargo, local) {
    cargo = (cargo || '').trim();
    local = (local || '').trim();
    if (_valido(cargo)) {
        const pares = _lerLista(_chavePares).filter(p => Array.isArray(p) && !(p[0] === cargo && p[1] === local));
        _gravar(_chavePares, [[cargo, local], ...pares].slice(0, 10));
        const v1 = _lerLista(_chaveCargos);
        if (!v1.some(x => x?.cargo === cargo)) {
            _gravar(_chaveCargos, [...v1, { id: _uid(), cargo }].slice(-10));
        }
    }
    if (_valido(local)) {
        const v1 = _lerLista(_chaveLocais);
        if (!v1.some(x => x?.nome === local)) {
            _gravar(_chaveLocais, [...v1, { id: _uid(), nome: local, buscaRecente: true }].slice(-10));
        }
    }
}

// "Continuar · você parou na vaga N" da home (v36): a última vaga aberta numa busca, com o endereço que a reabre.
const _chaveOndeParou = 'v2OndeParou';

export function guardarOndeParou(dados) {
    _gravar(_chaveOndeParou, dados);
}

export function ondeParou() {
    try {
        const dados = JSON.parse(localStorage.getItem(_chaveOndeParou) || 'null');
        return dados && dados.url && dados.n > 0 ? dados : null;
    } catch {
        return null;
    }
}

export function apagarBuscaRecente(indice) {
    const pares = _lerLista(_chavePares);
    if (pares.length) {
        pares.splice(indice, 1);
        _gravar(_chavePares, pares);
        return;
    }
    // O índice é o da lista mostrada (sem os itens inválidos do v1): apaga pelo texto.
    const alvo = buscasRecentes()[indice]?.[0];
    _gravar(_chaveCargos, _lerLista(_chaveCargos).filter(x => x?.cargo !== alvo));
}

// Mesmo slug do servidor (NormalizeString.SlugBusca): sem acento, minúsculo, o resto vira hífen.
export function slug(texto) {
    return (texto || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Landing do local como no resto do site (VagaBusiness.SlugDoLocal): só a cidade, e a região do DF sem o "- Brasília".
export function slugDoLocal(nome) {
    const cidade = (nome || '').split(',')[0];
    const i = cidade.indexOf(' - ');
    return slug(i > 0 ? cidade.slice(0, i) : cidade);
}

export function urlBusca(cargo, local) {
    // No hotsite, a busca da v1 dele (?query=&localidade=): lá a landing corta o "-em-" sem conferir, e um cargo com "em"
    // ("técnico em enfermagem") viraria cargo e local errados.
    if (config().hotsite) {
        // encodeURIComponent, como o qsDe da busca (o mesmo endereço, a mesma chave na borda).
        const partes = [];
        if ((cargo || '').trim()) {
            partes.push(`query=${encodeURIComponent(cargo.trim())}`);
        }
        if (local?.nome) {
            partes.push(`localidade=${encodeURIComponent(local.nome)}`);
        }
        return url(`/vagas${partes.length ? `?${partes.join('&')}` : ''}`);
    }
    const c = slug(cargo);
    const l = local ? (local.slug || slugDoLocal(local.nome)) : '';
    if (c && l) {
        return url(`/vagas-de-${c}-em-${l}`);
    }
    if (c) {
        return url(`/vagas-de-${c}`);
    }
    if (l) {
        return url(`/vagas-em-${l}`);
    }
    return url('/vagas');
}

export function localDaConexao() {
    const geo = obterGeo();
    if (!geo) {
        return null;
    }
    const nome = geo.pais === 'BR' && geo.regiaoCodigo ? `${geo.cidade}, ${geo.regiaoCodigo}` : geo.cidade;
    return { tipo: 'cidade', nome, slug: slugDoLocal(nome), origem: 'conexao' };
}

// Origem das "cidades perto de você": o ponto do aparelho (quem usou "Usar minha localização") ou o da conexão, na grade de
// 1 casa (~11 km), porque vai para uma URL pública e cacheada. Sem ponto, null.
export function pontoDeOrigem() {
    const escolhido = localEscolhido();
    const doAparelho = escolhido?.tipo === 'ponto';
    const ponto = doAparelho ? escolhido : obterGeo();
    const lat = Number(ponto?.lat);
    const lon = Number(ponto?.lon);
    if (!ponto || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180 || (lat === 0 && lon === 0)) {
        return null;
    }
    return { texto: `${lat.toFixed(1)},${lon.toFixed(1)}`, daConexao: !doAparelho };
}

// O ponto (GPS) é dado pessoal e envelhece: vale 6 horas, como o local da conexão. Cidade não expira.
const _validadePontoMs = 6 * 60 * 60 * 1000;

export function localEscolhido() {
    try {
        const local = JSON.parse(localStorage.getItem(_chaveEscolhido));
        if (!local || !local.nome) {
            return null;
        }
        if (local.tipo === 'ponto' && !(Date.now() - (local._ts || 0) < _validadePontoMs)) {
            localStorage.removeItem(_chaveEscolhido);
            return null;
        }
        return local;
    } catch {
        return null;
    }
}

export function escolherLocal(local) {
    try {
        if (local) {
            localStorage.setItem(_chaveEscolhido, JSON.stringify(local.tipo === 'ponto' ? { ...local, _ts: Date.now() } : local));
        } else {
            localStorage.removeItem(_chaveEscolhido);
        }
    } catch {
        // Sem armazenamento: vale só nesta página.
    }
}

// 2 casas (~1 km): o ponto é pessoal e vai para a URL e para a chave de cache arredondado.
const _arredondar = n => Math.round(n * 100) / 100;

export function pedirLocalizacao() {
    return new Promise((resolver, rejeitar) => {
        if (!navigator.geolocation) {
            rejeitar(new Error('sem geolocalização'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            p => resolver({ lat: _arredondar(p.coords.latitude), lon: _arredondar(p.coords.longitude) }),
            rejeitar,
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 });
    });
}
